import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import StatusPill from '../components/StatusPill';
import { formatDateTime, formatTime } from '../lib/dateFormat';
import {
  Bell, MessageCircle, MessageSquare, Users, CalendarDays, CheckCircle2,
  XCircle, Inbox, AlertTriangle, TrendingUp, Euro, Lock, MapPin, ArrowRight,
  UserPlus, Zap, Plus, Sparkles,
} from 'lucide-react';

export default function TodayPage() {
  const { t } = useTranslation();
  const { practice, user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/dashboard/heute');
      setData(r.data);
    } catch (_) {
      /* graceful */
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGroup = useCallback(async () => {
    if (!practice?.is_main_location) return;
    if ((practice?.plan || 'starter').toLowerCase() !== 'pro') return;
    try {
      const r = await api.get('/api/locations/group-summary');
      setGroup(r.data);
    } catch (_) { /* graceful */ }
  }, [practice]);

  useEffect(() => {
    load();
    loadGroup();
    const id = setInterval(() => { load(); loadGroup(); }, 10000);
    return () => clearInterval(id);
  }, [load, loadGroup]);

  const plan = (data?.plan || practice?.plan || 'starter').toLowerCase();
  const isPro = plan === 'pro';
  const isMain = !!practice?.is_main_location;
  const showLocations = isPro && isMain && (user?.role === 'admin' || user?.role === 'superadmin');

  const counts = data?.counts || {};
  const pro = data?.pro || {};

  return (
    <div data-testid="today-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('today.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('today.subtitle')}</p>
        </div>
        {isPro && (
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-semibold" data-testid="today-plan-pill">
            <Sparkles size={12}/> PRO
          </span>
        )}
      </div>

      {/* KPI strip (Starter: 5; Pro adds 2 more) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-row">
        <Kpi icon={<CalendarDays size={16}/>} label={t('today.kpi.appointmentsToday')} value={counts.appointments_today ?? 0} testId="kpi-appointments-today" tone="navy" />
        <Kpi icon={<CheckCircle2 size={16}/>} label={t('today.kpi.confirmed')} value={counts.confirmed ?? 0} testId="kpi-confirmed" tone="success" />
        <Kpi icon={<XCircle size={16}/>} label={t('today.kpi.cancelled')} value={counts.cancelled ?? 0} testId="kpi-cancelled" tone="danger" />
        <Kpi icon={<Users size={16}/>} label={t('today.kpi.waitlist')} value={counts.waitlist_active ?? 0} testId="kpi-waitlist" tone="neutral" />
        <Kpi icon={<Inbox size={16}/>} label={t('today.kpi.messagesToday')} value={counts.messages_today ?? 0} testId="kpi-messages-today" tone="accent" />
      </div>

      {/* Pro KPI strip */}
      {isPro ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="kpi-row-pro">
          <KpiBig
            icon={<Euro size={18}/>}
            label={t('today.kpi.savedRevenueToday')}
            value={`€ ${(pro.saved_revenue_today_eur || 0).toLocaleString('de-AT')}`}
            sub={`${t('today.pro.revenueMonthLabel')}: € ${(pro.saved_revenue_month_eur || 0).toLocaleString('de-AT')}`}
            testId="kpi-saved-revenue"
          />
          <KpiBig
            icon={<TrendingUp size={18}/>}
            label={t('today.kpi.forecastMonth')}
            value={`€ ${(pro.projected_next_month_eur || 0).toLocaleString('de-AT')}`}
            sub={t('today.pro.forecastHint')}
            testId="kpi-forecast"
          />
        </div>
      ) : (
        <UpgradeTile featureKey="pro_only_revenue" testId="upgrade-revenue" navigate={navigate} t={t} />
      )}

      {/* Quick actions */}
      <div className="so-card p-4" data-testid="quick-actions">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{t('today.quickActions.title')}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => navigate('/slot-aktivierung')} className="so-btn so-btn-primary" data-testid="qa-open-slot">
            <Zap size={14}/> {t('today.quickActions.openSlot')}
          </button>
          <button onClick={() => navigate('/warteliste')} className="so-btn so-btn-secondary" data-testid="qa-add-patient">
            <UserPlus size={14}/> {t('today.quickActions.addPatient')}
          </button>
          <button onClick={() => navigate('/termine')} className="so-btn so-btn-secondary" data-testid="qa-new-appointment">
            <Plus size={14}/> {t('today.quickActions.newAppointment')}
          </button>
        </div>
      </div>

      {/* Pro: No-Show Risk Summary */}
      {isPro ? (
        <RiskSummary count={pro.high_no_show_risk_count || 0} t={t} navigate={navigate} />
      ) : (
        <UpgradeTile featureKey="pro_only_risk" testId="upgrade-risk" navigate={navigate} t={t} />
      )}

      {/* Pro main-location: Multi-location overview */}
      {showLocations && (
        <LocationsOverview group={group} navigate={navigate} t={t} />
      )}

      {/* Main grid: upcoming, recent waitlist, messages */}
      <div className="grid lg:grid-cols-3 gap-6">
        <UpcomingList items={data?.upcoming_appointments || []} loading={loading} navigate={navigate} t={t} />
        <WaitlistActivity items={data?.recent_waitlist || []} loading={loading} navigate={navigate} t={t} />
        <MessagesFeed items={data?.recent_messages || []} loading={loading} t={t} />
      </div>
    </div>
  );
}

// ─── KPI tiles ────────────────────────────────────────────────────────────

function toneColor(tone) {
  switch (tone) {
    case 'success': return '#16A34A';
    case 'danger':  return '#DC2626';
    case 'accent':  return '#2B7FD4';
    case 'navy':    return '#1B3A6B';
    default:        return '#64748B';
  }
}

function Kpi({ icon, label, value, testId, tone = 'neutral' }) {
  const color = toneColor(tone);
  return (
    <div className="so-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold text-text-secondary">
        <span style={{ color }}>{icon}</span>
        <span className="line-clamp-1">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}

function KpiBig({ icon, label, value, sub, testId }) {
  return (
    <div className="so-card p-5 border-l-4 border-accent" data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold text-accent">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-2 text-3xl font-bold tabular-nums text-text">{value}</p>
      {sub && <p className="mt-1 text-xs text-text-secondary">{sub}</p>}
    </div>
  );
}

// ─── Pro: No-show risk summary ────────────────────────────────────────────

function RiskSummary({ count, t, navigate }) {
  if (!count || count === 0) {
    return (
      <div className="so-card p-4 flex items-center gap-3" data-testid="risk-summary-empty">
        <span className="text-success"><AlertTriangle size={18}/></span>
        <p className="text-sm text-text">{t('today.pro.riskNone')}</p>
      </div>
    );
  }
  return (
    <button
      onClick={() => navigate('/termine')}
      className="w-full so-card p-4 flex items-center justify-between gap-3 text-left hover:bg-danger/5 transition"
      style={{ borderLeft: '4px solid #DC2626' }}
      data-testid="risk-summary-badge"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center rounded-full bg-danger/10 text-danger w-9 h-9">
          <AlertTriangle size={18}/>
        </span>
        <div>
          <p className="text-xs uppercase tracking-wide font-semibold text-danger">{t('today.pro.riskSummaryTitle')}</p>
          <p className="text-sm font-semibold text-text mt-0.5">
            {t('today.pro.riskSummary', { count })}
          </p>
        </div>
      </div>
      <span className="text-danger text-xs font-semibold flex items-center gap-1">
        {t('today.pro.viewAppointments')} <ArrowRight size={14}/>
      </span>
    </button>
  );
}

// ─── Pro: Multi-location overview ─────────────────────────────────────────

function LocationsOverview({ group, navigate, t }) {
  const switchTo = (locId) => {
    localStorage.setItem('studioops_active_practice', locId);
    api.defaults.headers.common['X-Practice-Id'] = locId;
    // Hard reload so AuthContext picks up the new practice + banners refresh.
    window.location.reload();
  };
  const locations = group?.locations || [];
  if (locations.length <= 1) {
    return (
      <div className="so-card p-4" data-testid="locations-overview-empty">
        <h2 className="font-semibold flex items-center gap-2"><MapPin size={16} className="text-accent"/>{t('today.pro.locationsTitle')}</h2>
        <p className="mt-2 text-sm text-text-secondary">{t('today.pro.locationsEmpty')}</p>
      </div>
    );
  }
  return (
    <div className="so-card overflow-hidden" data-testid="locations-overview">
      <div className="px-5 py-4 border-b border-border">
        <h2 className="font-semibold flex items-center gap-2"><MapPin size={16} className="text-accent"/>{t('today.pro.locationsTitle')}</h2>
        <p className="mt-1 text-xs text-text-secondary">{t('today.pro.locationsHint')}</p>
      </div>
      <table className="so-table">
        <thead>
          <tr>
            <th>{t('today.pro.tableLocation')}</th>
            <th className="text-right">{t('today.pro.tableAppointments')}</th>
            <th className="text-right">{t('today.pro.tableWaitlist')}</th>
            <th className="text-right">{t('today.pro.tableMessages')}</th>
            <th>{t('today.pro.tableStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((l) => (
            <tr
              key={l._id}
              onClick={() => switchTo(l._id)}
              className="cursor-pointer hover:bg-accent/5"
              data-testid={`location-row-${l._id}`}
            >
              <td>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{l.location_label || l.name}</span>
                  {l.is_main_location && (
                    <span className="inline-flex items-center text-[10px] uppercase tracking-wide font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded">
                      ★ {t('today.pro.hauptstandort')}
                    </span>
                  )}
                </div>
              </td>
              <td className="text-right tabular-nums">{l.usage?.appointments_today ?? 0}</td>
              <td className="text-right tabular-nums">{l.usage?.waitlist ?? 0}</td>
              <td className="text-right tabular-nums">{l.usage?.messages_today ?? 0}</td>
              <td>
                <span className={`so-pill ${l.deactivated ? 'so-pill-danger' : 'so-pill-success'}`}>
                  {l.deactivated ? t('today.pro.statusInactive') : t('today.pro.statusActive')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Lists ────────────────────────────────────────────────────────────────

function UpcomingList({ items, loading, navigate, t }) {
  return (
    <div className="so-card overflow-hidden" data-testid="upcoming-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2"><Bell size={16} className="text-accent"/>{t('today.upcoming.title')}</h2>
        <button onClick={() => navigate('/termine')} className="text-xs text-accent font-semibold hover:underline">{t('today.upcoming.viewAll')}</button>
      </div>
      {loading ? null : items.length === 0 ? (
        <div className="p-8 text-center text-text-secondary text-sm">{t('today.upcoming.empty')}</div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((a) => (
            <li key={a.id} className="px-5 py-3 text-sm" data-testid={`upcoming-${a.id}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-xs text-text-secondary">{formatDateTime(a.starts_at)}</span>
                <StatusPill status={a.status || 'pending'} label={t(`appointments.status.${a.status || 'pending'}`, { defaultValue: a.status || '' })} />
              </div>
              <p className="mt-0.5 font-medium text-text">{a.patient_first_name} {a.patient_last_name}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WaitlistActivity({ items, loading, navigate, t }) {
  return (
    <div className="so-card overflow-hidden" data-testid="waitlist-activity-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2"><Users size={16} className="text-accent"/>{t('today.waitlistActivity.title')}</h2>
        <button onClick={() => navigate('/warteliste')} className="text-xs text-accent font-semibold hover:underline">{t('today.waitlistActivity.viewAll')}</button>
      </div>
      {loading ? null : items.length === 0 ? (
        <div className="p-8 text-center text-text-secondary text-sm">{t('today.waitlistActivity.empty')}</div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((p) => (
            <li key={p.id} className="px-5 py-3 text-sm" data-testid={`waitlist-recent-${p.id}`}>
              <p className="font-medium text-text">{p.first_name} {p.last_name}</p>
              <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-text-secondary">
                <span>{t('today.waitlistActivity.addedAt')}: {formatDateTime(p.created_at)}</span>
                <span className="font-mono">Trust {p.trust_score}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MessagesFeed({ items, loading, t }) {
  return (
    <div className="so-card overflow-hidden" data-testid="messages-feed-card">
      <div className="px-5 py-4 border-b border-border">
        <h2 className="font-semibold flex items-center gap-2"><MessageCircle size={16} className="text-accent"/>{t('today.messagesFeed.title')}</h2>
      </div>
      {loading ? null : items.length === 0 ? (
        <div className="p-8 text-center text-text-secondary text-sm">{t('today.messagesFeed.empty')}</div>
      ) : (
        <ul className="divide-y divide-border max-h-[420px] overflow-y-auto">
          {items.map((m) => (
            <li key={m.id} className="px-5 py-3 flex items-start gap-3 text-sm" data-testid={`message-${m.id}`}>
              <span className={`mt-0.5 ${m.direction === 'inbound' ? 'text-accent' : 'text-text-secondary'}`}>
                {m.channel === 'whatsapp' ? <MessageCircle size={14}/> : <MessageSquare size={14}/>}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[11px] uppercase tracking-wide" style={{ color: m.direction === 'inbound' ? '#2B7FD4' : '#64748B' }}>
                    {m.direction === 'inbound' ? t('today.messagesFeed.in') : t('today.messagesFeed.out')} · {m.channel}
                  </span>
                  <span className="text-text-secondary text-[11px] font-mono">{formatTime(m.created_at)}</span>
                  {m.status && <span className="text-[11px] text-text-secondary">{m.status}</span>}
                </div>
                <p className="text-text mt-0.5 line-clamp-2 break-words">{m.body || '—'}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Upgrade tile (Starter sees Pro feature) ──────────────────────────────

function UpgradeTile({ featureKey, testId, navigate, t }) {
  return (
    <div className="so-card p-5 flex items-center justify-between gap-4 bg-accent/5 border-accent/30" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center rounded-full bg-accent/10 text-accent w-10 h-10">
          <Lock size={18}/>
        </span>
        <div>
          <p className="text-sm font-semibold text-text">{t('today.upgrade.title')}</p>
          <p className="text-xs text-text-secondary mt-0.5">{t('today.upgrade.body')}</p>
        </div>
      </div>
      <button
        onClick={() => navigate('/einstellungen/abo')}
        className="so-btn so-btn-primary"
        data-testid={`${testId}-cta`}
      >
        {t('today.upgrade.cta')} <ArrowRight size={14}/>
      </button>
    </div>
  );
}
